/*
 * Autorska prava pridržava Mali mol d.o.o. Pula
 * Copyright (c) 2016. by Mali mol d.o.o. Pula. All rights reserved.
 *
 * Korištenje ili javno objavljivanje ovog programskog koda ili bilo kojeg
 * njegovog dijela, nije dozvoljeno bez prethodnog pismenog odobrenja tvrtke Mali mol d.o.o.
 *
 * Mali mol d.o.o., Špiljavac 2, 52100 Pula, Croatia
 * <URL:http://www.malimol.hr>, <mailto:info@malimol.hr>
 */

import { useI18n } from "vue-i18n";
import {
  ResponseError,
  IResponseErrorOptions,
} from "@/types/ResponseError";

interface IValidationParams {
  attribute: boolean;
  options?: string[];
}

interface IValidation {
  name: string;
  params: IValidationParams;
}

const validationParams = [
  { name: "constraint_delete", params: { attribute: false } },
  { name: "constraint_update", params: { attribute: false } },
  { name: "workshop_not_found", params: { attribute: false } },
  { name: "office_not_found", params: { attribute: false } },
  { name: "delivery_address_not_found", params: { attribute: false } },
  { name: "accepted", params: { attribute: true } },
  {
    name: "accepted_if",
    params: { attribute: true, options: ["other", "value"] },
  },
  { name: "active_url", params: { attribute: true } },
  { name: "after", params: { attribute: true, options: ["date"] } },
  { name: "after_or_equal", params: { attribute: true, options: ["date"] } },
  { name: "alpha", params: { attribute: true } },
  { name: "alpha_dash", params: { attribute: true } },
  { name: "alpha_num", params: { attribute: true } },
  { name: "array", params: { attribute: true } },
  { name: "ascii", params: { attribute: true } },
  { name: "before", params: { attribute: true, options: ["date"] } },
  { name: "before_or_equal", params: { attribute: true, options: ["date"] } },
  {
    name: "between_array",
    params: { attribute: true, options: ["min", "max"] },
  },
  {
    name: "between_file",
    params: { attribute: true, options: ["min", "max"] },
  },
  {
    name: "between_numeric",
    params: { attribute: true, options: ["min", "max"] },
  },
  {
    name: "between_string",
    params: { attribute: true, options: ["min", "max"] },
  },
  { name: "boolean", params: { attribute: true } },
  { name: "can", params: { attribute: true } },
  { name: "confirmed", params: { attribute: true } },
  { name: "current_password", params: { attribute: false } },
  { name: "date", params: { attribute: true } },
  { name: "date_equals", params: { attribute: true, options: ["date"] } },
  { name: "date_format", params: { attribute: true, options: ["format"] } },
  { name: "decimal", params: { attribute: true, options: ["decimal"] } },
  { name: "declined", params: { attribute: true } },
  {
    name: "declined_if",
    params: { attribute: true, options: ["other", "value"] },
  },
  { name: "different", params: { attribute: true, options: ["other"] } },
  { name: "digits", params: { attribute: true, options: ["digits"] } },
  {
    name: "digits_between",
    params: { attribute: true, options: ["min", "max"] },
  },
  { name: "dimensions", params: { attribute: true } },
  { name: "distinct", params: { attribute: true } },
  { name: "doesnt_end_with", params: { attribute: true, options: ["values"] } },
  {
    name: "doesnt_start_with",
    params: { attribute: true, options: ["values"] },
  },
  { name: "email", params: { attribute: true } },
  { name: "ends_with", params: { attribute: true, options: ["values"] } },
  { name: "enum", params: { attribute: true } },
  { name: "exists", params: { attribute: true } },
  { name: "file", params: { attribute: true } },
  { name: "filled", params: { attribute: true } },
  { name: "gt_array", params: { attribute: true, options: ["value"] } },
  { name: "gt_file", params: { attribute: true, options: ["value"] } },
  { name: "gt_numeric", params: { attribute: true, options: ["value"] } },
  { name: "gt_string", params: { attribute: true, options: ["value"] } },
  { name: "gte_array", params: { attribute: true, options: ["value"] } },
  { name: "gte_file", params: { attribute: true, options: ["value"] } },
  { name: "gte_numeric", params: { attribute: true, options: ["value"] } },
  { name: "gte_string", params: { attribute: true, options: ["value"] } },
  { name: "image", params: { attribute: true } },
  { name: "in", params: { attribute: true } },
  { name: "in_array", params: { attribute: true, options: ["other"] } },
  { name: "integer", params: { attribute: true } },
  { name: "ip", params: { attribute: true } },
  { name: "ipv4", params: { attribute: true } },
  { name: "ipv6", params: { attribute: true } },
  { name: "json", params: { attribute: true } },
  { name: "lowercase", params: { attribute: true } },
  { name: "lt_array", params: { attribute: true, options: ["value"] } },
  { name: "lt_file", params: { attribute: true, options: ["value"] } },
  { name: "lt_numeric", params: { attribute: true, options: ["value"] } },
  { name: "lt_string", params: { attribute: true, options: ["value"] } },
  { name: "lte_array", params: { attribute: true, options: ["value"] } },
  { name: "lte_file", params: { attribute: true, options: ["value"] } },
  { name: "lte_numeric", params: { attribute: true, options: ["value"] } },
  { name: "lte_string", params: { attribute: true, options: ["value"] } },
  { name: "mac_address", params: { attribute: true } },
  { name: "max_array", params: { attribute: true, options: ["max"] } },
  { name: "max_file", params: { attribute: true, options: ["max"] } },
  { name: "max_numeric", params: { attribute: true, options: ["max"] } },
  { name: "max_string", params: { attribute: true, options: ["max"] } },
  { name: "max_digits", params: { attribute: true, options: ["max"] } },
  { name: "mimes", params: { attribute: true, options: ["values"] } },
  { name: "mimetypes", params: { attribute: true, options: ["values"] } },
  { name: "min_array", params: { attribute: true, options: ["min"] } },
  { name: "min_file", params: { attribute: true, options: ["min"] } },
  { name: "min_numeric", params: { attribute: true, options: ["min"] } },
  { name: "min_string", params: { attribute: true, options: ["min"] } },
  { name: "min_digits", params: { attribute: true, options: ["min"] } },
  { name: "missing", params: { attribute: true } },
  {
    name: "missing_if",
    params: { attribute: true, options: ["other", "value"] },
  },
  {
    name: "missing_unless",
    params: { attribute: true, options: ["other", "value"] },
  },
  { name: "missing_with", params: { attribute: true, options: ["values"] } },
  {
    name: "missing_with_all",
    params: { attribute: true, options: ["values"] },
  },
  {
    name: "multiple_of",
    params: { attribute: true, options: ["value"] },
  },
  { name: "not_in", params: { attribute: true } },
  { name: "not_regex", params: { attribute: true } },
  { name: "numeric", params: { attribute: true } },
  { name: "password_letters", params: { attribute: true } },
  { name: "password_mixed", params: { attribute: true } },
  { name: "password_numbers", params: { attribute: true } },
  { name: "password_symbols", params: { attribute: true } },
  { name: "password_uncompromised", params: { attribute: true } },
  { name: "present", params: { attribute: true } },
  { name: "prohibited", params: { attribute: true } },
  {
    name: "prohibited_if",
    params: { attribute: true, options: ["other", "value"] },
  },
  {
    name: "prohibited_unless",
    params: { attribute: true, options: ["other", "values"] },
  },
  {
    name: "prohibits",
    params: { attribute: true, options: ["other"] },
  },
  { name: "regex", params: { attribute: true } },
  { name: "required", params: { attribute: true } },
  {
    name: "required_array_keys",
    params: { attribute: true, options: ["values"] },
  },
  {
    name: "required_if",
    params: { attribute: true, options: ["other", "value"] },
  },
  {
    name: "required_if_accepted",
    params: { attribute: true, options: ["other"] },
  },
  {
    name: "required_unless",
    params: { attribute: true, options: ["other", "values"] },
  },
  {
    name: "required_with",
    params: { attribute: true, options: ["values"] },
  },
  {
    name: "required_with_all",
    params: { attribute: true, options: ["values"] },
  },
  {
    name: "required_without",
    params: { attribute: true, options: ["values"] },
  },
  {
    name: "required_without_all",
    params: { attribute: true, options: ["values"] },
  },
  {
    name: "same",
    params: { attribute: true, options: ["other"] },
  },
  {
    name: "size_array",
    params: { attribute: true, options: ["size"] },
  },
  {
    name: "size_file",
    params: { attribute: true, options: ["size"] },
  },
  {
    name: "size_numeric",
    params: { attribute: true, options: ["size"] },
  },
  {
    name: "size_string",
    params: { attribute: true, options: ["size"] },
  },
  {
    name: "starts_with",
    params: { attribute: true, options: ["values"] },
  },
  {
    name: "string",
    params: { attribute: true },
  },
  {
    name: "timezone",
    params: { attribute: true },
  },
  {
    name: "unique",
    params: { attribute: true },
  },
  {
    name: "uploaded",
    params: { attribute: true },
  },
  {
    name: "uppercase",
    params: { attribute: true },
  },
  {
    name: "url",
    params: { attribute: true },
  },
  {
    name: "ulid",
    params: { attribute: true },
  },
  {
    name: "uuid",
    params: { attribute: true },
  },
] as IValidation[];

export const useResponseParser = () => {
  const { t } = useI18n();

  const processErrorResponse = (response: any) => {
      if (response.reason) {
        let validation = validationParams.find((item) => item.name === response.reason);
        if(validation) {
          if(!validation.params.attribute) {
            // Nema atributa i nema parametara - samo poruka
            throw new TypeError(t("validate." + response.reason));
          }

          // Imamo makar atribut - sad da vidimo imamo li dodatne parametre
          if(response.message) {
            let parameters = response.message.split(' ');
            if(parameters.length > 0) {
              let attribute = parameters.shift();
              let params: any = {};
              params.attribute = attribute;
              if(validation.params.options) {
                let options = validation.params.options;
                options.forEach((item, index) => {
                  params[item] = parameters[index];
                });
              }
              let options = { parameter: attribute } as IResponseErrorOptions;
              throw new ResponseError(
                t("validate." + response.reason, params),
                options
              );
            }
          }
        }
      }
      console.log(response);
      throw new ResponseError(t("message.invalid_server_response"));
  }

  return {
    processErrorResponse,
  };
}
